/*
 * ApolloApp.tsx
 *
 * Stellt sicher, dass ein Apollo und urql Client aufgesetzt ist
 * und per ApolloProvider den Kindern zur Verfügung steht.
 *
 * Setzt AUCH einen https://formidable.com/open-source/urql/ client auf.
 *
 * Created by Dr. Maximillian Dornseif 2021-08-12 in huWaWi3
 * Copyright (c) 2021, 2022 HUDORA GmbH
 */

import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { relayStylePagination } from '@apollo/client/utilities';
import { useAuth0 } from '@auth0/auth0-react';
import { HdErrorBoundary3 } from '@hudora/hd-error-boundary';
import * as Sentry from '@sentry/react';
import { devtoolsExchange } from '@urql/devtools';
import React, { useEffect, useState } from 'react';
import { Client, Provider, cacheExchange, createClient, fetchExchange, mapExchange } from 'urql';

// import { setLogVerbosity } from "@apollo/client";
// setLogVerbosity("log"); // display all messages
// setLogVerbosity("warn"); // display only warnings and errors (default)
// setLogVerbosity("error"); // display only errors
// setLogVerbosity("silent"); // hide all console messages

export function ApolloApp(props) {
  const { getAccessTokenSilently } = useAuth0();
  const [apClient, setApClient] = useState<ApolloClient<any>>();
  const [urqlClient, setUrqlClient] = useState<Client>();

  useEffect(() => {
    let mounted = true;
    async function buildClient() {
      const token = await getAccessTokenSilently();

      const newUrqlClient = createClient({
        url: process.env.REACT_APP_GRAPHQL_URL,
        exchanges: [
          devtoolsExchange,
          cacheExchange,
          fetchExchange,
          mapExchange({
            onError(error, operation) {
              console.log(`The operation ${operation.key} has errored with:`, error, operation);
            },
          }),
        ],
        fetchOptions: () => {
          return {
            headers: { authorization: token ? `Bearer ${token}` : '' },
          };
        },
      });
      setUrqlClient(newUrqlClient);

      const newApolloClient = new ApolloClient({
        // timeout: 30000,
        connectToDevTools: true,
        cache,
        link: apErrLink.concat(
          new HttpLink({
            uri: process.env.REACT_APP_GRAPHQL_URL,
            // useGETForQueries: true,
            headers: {
              authorization: token ? `Bearer ${token}` : 'MISSING',
              frontend: process.env.GAE_SERVICE ?? 'dev',
            },
          })
        ),
        version: process.env.REACT_APP_VERSION,
        name: process.env.REACT_APP_NAME,
        defaultOptions: {
          watchQuery: {
            errorPolicy: 'all',
            // fetchPolicy: 'cache-and-network',
            nextFetchPolicy: 'cache-first',
          },
          query: {
            errorPolicy: 'all',
            // fetchPolicy: 'cache-and-network',
          },
          mutate: {
            errorPolicy: 'all',
          },
        },
      });
      if (mounted) {
        setApClient(newApolloClient);
      }
    }

    buildClient();

    return () => {
      mounted = false;
    };
  }, []);

  if (!apClient) {
    const ts = +new Date();

    return (
      <div>
        Warte auf Authentifizierungs-Token / Erstellung des Apollo-Clients. Wenn diese Nachricht nicht
        verschwinden will: <a href={`/ts=${ts}`}>Seite neu laden</a>.
      </div>
    );
  }

  return (
    <HdErrorBoundary3>
      <Provider value={urqlClient}>
        <ApolloProvider client={apClient}>{props.children}</ApolloProvider>
      </Provider>
    </HdErrorBoundary3>
  );
}

function _errHandler({ graphQLErrors, networkError }: ErrorResponse) {
  // https://www.apollographql.com/docs/react/data/error-handling/
  if (graphQLErrors) {
    for (const error of graphQLErrors) {
      if (process.env.NODE_ENV === 'production') {
        Sentry.captureException(error);
      } else {
        const { message, locations, path, extensions } = error;
        const { code, exception } = extensions || {};
        console.error(
          `[GraphQL error ${code}]: Message: ${message}, Path: ${path}`,
          (exception as any)?.stacktrace,
          locations,
          error
        );
      }
    }
  }

  if (networkError) {
    if (process.env.NODE_ENV === 'production') {
      Sentry.captureException(networkError);
    } else {
      console.error(`[Network error]: ${networkError}`);
    }
    // Zum späteren Zugriff sichern
    (networkError as any).hdGraphQLErrors = graphQLErrors;
  }
}

const apErrLink = onError(_errHandler);

// Globales Error-Handling
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        belege: relayStylePagination(),
        belegPages: relayStylePagination(),
        aaaUserPages: relayStylePagination(),
        aaaPermissionPages: relayStylePagination(),
        aaaRolePages: relayStylePagination(),
        h3AuftragsEntwurfPages: relayStylePagination(),
        h3BelegPages: relayStylePagination(),
        h3DbAuditPages: relayStylePagination(),
        h3EinkaufsBelegPages: relayStylePagination(),
        h3ItemInfoPages: relayStylePagination(),
        h3ItemPages: relayStylePagination(),
        h3KundeInfoPages: relayStylePagination(),
        h3ProduktInfoPages: relayStylePagination(),
        h3ProduktPages: relayStylePagination(),
        h3SkuMasterPages: relayStylePagination(),
        h3SkuPages: relayStylePagination(),
        hwAuftragPages: relayStylePagination(),
        hwBestellungPages: relayStylePagination(),
        hwEinkaufspreisPages: relayStylePagination(),
        hwIcBestandsbuchEintragPages: relayStylePagination(),
        hwIcIntventurBestaetigungPages: relayStylePagination(),
        hwIcNeuverbuchungswunschPages: relayStylePagination(),
        hwIcWertkorrekturPages: relayStylePagination(),
        hwIcZuteilungPages: relayStylePagination(),
        hwKommiAuftrag2Pages: relayStylePagination(),
        hwKunden: relayStylePagination(),
        hwKundenArtNrPages: relayStylePagination(),
        hwKundenGruppen: relayStylePagination(),
        hwKundenGruppePages: relayStylePagination(),
        hwKundenPreisPages: relayStylePagination(),
        hwKundenVerbandPages: relayStylePagination(),
        hwKundePages: relayStylePagination(),
        hwLagerPages: relayStylePagination(),
        hwLieferanten: relayStylePagination(),
        hwLieferantPages: relayStylePagination(),
        hwPreise: relayStylePagination(),
        hwPreisPages: relayStylePagination(),
        hwReservierungPages: relayStylePagination(),
        hwReservierungsPositionPages: relayStylePagination(),
      },
    },
  },
});
